import { canProbeMediaFile, type ProbedMedia, probeMediaFile } from '@lib/media/mediaProbe';
import { isImageItem } from '@lib/utils/fileExtensions';

export const SUPPORTED_VIDEO_MIME_TYPES = new Set(['video/mp4', 'video/quicktime', 'video/webm']);

export const SUPPORTED_VIDEO_EXTENSIONS = /\.(mp4|mov|webm)$/i;

/**
 * Checks if a file is a supported video format
 */
export const isSupportedVideoFile = (file: File): boolean => {
  return (
    (file.type && SUPPORTED_VIDEO_MIME_TYPES.has(file.type)) ||
    SUPPORTED_VIDEO_EXTENSIONS.test(file.name)
  );
};

/**
 * Duration and display dimensions of a media file, read before upload. A `<video>` or `<audio>`
 * element reads what the browser can play; whatever it can't (an unplayable container, or a
 * playable one holding a codec it can't decode, like ProRes or HEVC in Chrome) falls back to
 * MediaInfo. An image gets dimensions only, from `<img>`, and nothing when the browser can't
 * decode it (HEIC outside Safari). Undefined for unrecognised files. Never rejects, so a failed
 * read can't take the file's other metadata down with it.
 */
export const getMediaInfo = async (file: File): Promise<ProbedMedia | undefined> => {
  if (isImageItem(file)) return readImageDimensions(file);

  const isVideo = isSupportedVideoFile(file);
  const fromBrowser = isVideo
    ? await readWithMediaElement(file, 'video')
    : file.type.startsWith('audio/')
      ? await readWithMediaElement(file, 'audio')
      : undefined;
  const hasEverything =
    fromBrowser?.durationSeconds !== undefined && (!isVideo || fromBrowser.width !== undefined);
  if (hasEverything || !(isVideo || canProbeMediaFile(file.name))) return fromBrowser;

  try {
    return { ...(await probeMediaFile(file)), ...fromBrowser };
  } catch (error) {
    console.warn(`Could not probe "${file.name}":`, error);
    return fromBrowser;
  }
};

// `naturalWidth`/`naturalHeight` follow EXIF orientation, so they are display-oriented like the
// rest of `ProbedMedia`.
const readImageDimensions = (file: File): Promise<ProbedMedia | undefined> => {
  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const { naturalWidth: width, naturalHeight: height } = img;
      resolve(width > 0 && height > 0 ? { width, height } : undefined);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(undefined);
    };
    img.src = objectUrl;
  });
};

const readWithMediaElement = (
  file: File,
  kind: 'video' | 'audio',
): Promise<ProbedMedia | undefined> => {
  return new Promise((resolve) => {
    try {
      const element = document.createElement(kind);
      element.preload = 'metadata';
      element.muted = true;
      const objectUrl = URL.createObjectURL(file);

      const cleanup = () => {
        URL.revokeObjectURL(objectUrl);
        element.remove();
      };

      let resolved = false;

      const handleResolution = (value: ProbedMedia | undefined) => {
        if (!resolved) {
          resolved = true;
          cleanup();
          resolve(value);
        }
      };

      // Set a timeout to avoid hanging if metadata never loads
      const timeoutId = setTimeout(() => {
        handleResolution(undefined);
      }, 10000);

      element.onloadedmetadata = () => {
        clearTimeout(timeoutId);
        const probed: ProbedMedia = {};
        if (Number.isFinite(element.duration)) probed.durationSeconds = element.duration;
        if (
          element instanceof HTMLVideoElement &&
          element.videoWidth > 0 &&
          element.videoHeight > 0
        ) {
          probed.width = element.videoWidth;
          probed.height = element.videoHeight;
        }
        handleResolution(probed);
      };

      element.onerror = () => {
        clearTimeout(timeoutId);
        handleResolution(undefined);
      };

      element.src = objectUrl;
    } catch (e) {
      resolve(undefined);
    }
  });
};

/**
 * Extracts a thumbnail frame from a video file as a data URL
 * Returns undefined if the file is not a supported video or thumbnail cannot be generated
 * @param file - The video file to extract thumbnail from
 * @param quality - JPEG quality (0-1), default 0.8
 * @param timestampSeconds - Frame time in seconds to extract, default 0 (first frame)
 */
export const getVideoThumbnail = (
  file: File,
  quality: number = 0.8,
  timestampSeconds: number = 0,
  maxDimension?: number,
): Promise<string | undefined> => {
  return new Promise((resolve) => {
    if (!isSupportedVideoFile(file)) {
      resolve(undefined);
      return;
    }

    try {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.crossOrigin = 'anonymous';
      const objectUrl = URL.createObjectURL(file);

      const cleanup = () => {
        URL.revokeObjectURL(objectUrl);
        video.remove();
      };

      let resolved = false;

      const handleResolution = (value: string | undefined) => {
        if (!resolved) {
          resolved = true;
          cleanup();
          resolve(value);
        }
      };

      const extractThumbnail = () => {
        try {
          const w = video.videoWidth;
          const h = video.videoHeight;
          // Downscale to maxDimension if provided — keeps the data URL small
          // enough to hold N copies in memory across a batch upload while
          // still producing a recognizable preview at typical card sizes.
          const scale = maxDimension ? Math.min(1, maxDimension / Math.max(w, h)) : 1;
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(w * scale));
          canvas.height = Math.max(1, Math.round(h * scale));

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            handleResolution(undefined);
            return;
          }

          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL('image/jpeg', quality);
          handleResolution(dataUrl);
        } catch (error) {
          handleResolution(undefined);
        }
      };

      // Set a timeout to avoid hanging if metadata never loads
      const timeoutId = setTimeout(() => {
        handleResolution(undefined);
      }, 10000);

      video.onloadedmetadata = () => {
        clearTimeout(timeoutId);
        // Seek to the desired timestamp
        video.currentTime = timestampSeconds;
      };

      video.onseeked = () => {
        extractThumbnail();
      };

      video.onerror = () => {
        clearTimeout(timeoutId);
        handleResolution(undefined);
      };

      video.src = objectUrl;
    } catch (e) {
      resolve(undefined);
    }
  });
};

/**
 * Generates a Low-Quality Image Placeholder (LQIP) as a tiny PNG data URL.
 * Accepts either a data URL string (e.g. from getVideoThumbnail) or a File/Blob.
 * Returns undefined on failure.
 * @param source - Data URL string or File/Blob to downscale
 * @param maxDimension - Max width or height in pixels (default 4 — just a few pixels, blurred up by CSS)
 */
export const generateLQIP = (
  source: string | File | Blob,
  maxDimension: number = 4,
): Promise<string | undefined> => {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      const isBlob = source instanceof Blob;
      const objectUrl = isBlob ? URL.createObjectURL(source) : undefined;

      const cleanup = () => {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      };

      img.onload = () => {
        try {
          const { naturalWidth: w, naturalHeight: h } = img;
          if (w === 0 || h === 0) {
            cleanup();
            resolve(undefined);
            return;
          }

          const scale = maxDimension / Math.max(w, h);
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(1, Math.round(w * scale));
          canvas.height = Math.max(1, Math.round(h * scale));

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            cleanup();
            resolve(undefined);
            return;
          }

          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL('image/png');
          cleanup();
          resolve(dataUrl);
        } catch {
          cleanup();
          resolve(undefined);
        }
      };

      img.onerror = () => {
        cleanup();
        resolve(undefined);
      };

      img.src = isBlob ? objectUrl! : (source as string);
    } catch {
      resolve(undefined);
    }
  });
};

/**
 * Generates a tiny PNG LQIP thumbnail for an image file.
 * Returns undefined if the file is not an image or LQIP generation fails.
 */
export const getImageThumbnail = (file: File): Promise<string | undefined> => {
  if (!file.type.startsWith('image/')) {
    return Promise.resolve(undefined);
  }
  return generateLQIP(file);
};
